import React from 'react';
import { createFileRoute, useSearch, Link } from '@tanstack/react-router';
import { useDashboards } from '../../api/dashboard';
import { LoadingState } from '../../components/common/LoadingState';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { PlusIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { PleriClient } from '../../components/Pleri/client';
import { useGetTenantIntegrations } from '../../components/onboarding/apis/tenant-integrations';

import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/dashboard/')({
  component: DashboardListComponent,
});

interface _DashboardSearchParams {
  tenantId?: string;
  ownerId?: string;
  scope?: 'user' | 'team' | 'tenant';
}

function DashboardListComponent() {
  const { t } = useTranslation('pleri/pages');
  const _search = useSearch({ from: '/dashboard/' });

  // Fetch dashboard list
  const dashboardsQuery = useDashboards({
    limit: 50,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetTenantIntegrations();

  const hasEnabledGithubIntegration =
    !isLoadingIntegrations &&
    integrations?.some(
      (integration) =>
        integration.provider?.toLowerCase() === 'github' && integration.enabled,
    );

  // Handler for creating a new dashboard via Pleri chat
  const handleCreateDashboard = () => {
    PleriClient.updateChatInput({
      prompt:
        'I want to create a new dashboard. Can you help me build a custom dashboard with visualizations and metrics that would be useful for my security operations?',
      useExistingConversation: false,
      autoSubmit: false,
    });
  };

  // Loading state
  if (dashboardsQuery.isLoading) {
    return (
      <div className="flex-1 p-6">
        <LoadingState text={t('loadingDashboards')} />
      </div>
    );
  }

  // Error state
  if (dashboardsQuery.error) {
    return (
      <div className="flex-1 p-6">
        <ErrorMessage
          message={
            (dashboardsQuery.error as Error)?.message ||
            t('failedToLoadDashboards')
          }
          onDismiss={() => {}}
        />
      </div>
    );
  }

  const { dashboards = [] } = dashboardsQuery.data || {};

  const activeDashboards = dashboards.filter(
    (dashboard) =>
      dashboard.dashboardId !== 'dash-002-code' || hasEnabledGithubIntegration,
  );

  const activeDashboardsCount = activeDashboards.length;

  return (
    <div className="flex-1 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
              {t('dashboards')}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {activeDashboardsCount === 0
                ? t('createYourFirstDashboardToVisualizeYour')
                : t('dashboardsAvailable', { count: activeDashboardsCount })}
            </p>
          </div>
          <button
            onClick={handleCreateDashboard}
            className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            disabled={true}
          >
            <PlusIcon className="h-4 w-4" />
            {t('createDashboardComingSoon')}
          </button>
        </div>
      </div>
      {/* Dashboard Grid */}
      {activeDashboardsCount === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="mb-4 rounded-full bg-gray-100 p-4 dark:bg-gray-800">
            <ChartBarIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
            {t('noDashboardsYet')}
          </h3>
          <p className="mb-6 max-w-md text-center text-gray-600 dark:text-gray-400">
            {t('getStartedByCreatingYourFirstDashboard')}
          </p>
          <button
            onClick={handleCreateDashboard}
            className="rounded-lg bg-orange-600 px-6 py-3 text-sm font-medium text-white hover:bg-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:outline-none"
          >
            {t('createYourFirstDashboard')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {activeDashboards?.map((dashboard) => (
            <DashboardCard key={dashboard.dashboardId} dashboard={dashboard} />
          ))}
        </div>
      )}
    </div>
  );
}

// Dashboard card component
interface DashboardCardProps {
  dashboard: import('../../api/dashboard').Dashboard;
}

function DashboardCard({ dashboard }: DashboardCardProps) {
  const { t } = useTranslation('pleri/pages');
  return (
    <Link
      to="/dashboard/$dashboardId"
      params={{ dashboardId: dashboard.dashboardId }}
      search={{
        tenantId: dashboard.tenantId,
        ownerId: dashboard.ownerId,
        scope: dashboard.scope,
      }}
      className="group block rounded-lg border border-gray-200 bg-white p-6 transition-all duration-200 hover:border-gray-300 hover:shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
    >
      <div className="mb-4 flex items-start justify-between">
        <div className="flex-1">
          <h3 className="mb-2 text-lg font-semibold text-gray-900 transition-colors group-hover:text-orange-600 dark:text-gray-100">
            {dashboard.title}
          </h3>
          <p className="line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
            {dashboard.description}
          </p>
        </div>
        <span
          className={`ml-2 flex-shrink-0 rounded-full px-2 py-1 text-xs font-medium ${
            dashboard.status === 'published'
              ? 'bg-green-100 text-green-800 dark:bg-green-900'
              : dashboard.status === 'draft'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
          }`}
        >
          {dashboard.status}
        </span>
      </div>
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <span>{new Date(dashboard.createdAt).toLocaleDateString()}</span>
        <span className="capitalize">{dashboard.scope}</span>
      </div>
      {dashboard.dashboardSchema?.fileUrl && (
        <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
          <a
            href={dashboard.dashboardSchema.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            onClick={(e) => e.stopPropagation()}
          >
            <span>{t('downloadPdf')}</span>
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        </div>
      )}
    </Link>
  );
}
